/**
 * The Special prices card (DNKADM-0493) — weekday, date and coach prices for each of the
 * service's lengths, read-only, with ONE Edit that opens a drawer. Collapsed to one line
 * ("No special prices") when no length has any.
 *
 * **Reads.** The same GETs the three editors already make when they open
 * (`weekly-prices`, `date-prices`, `staff-prices` per length) plus the lengths list —
 * made up front so the card can say what is set. No new endpoint, no new field. Each
 * length's price reads are best-effort: one failing marks only that length "Couldn't load its
 * special prices" and the Edit stays on (the editors in the drawer load on their own, each
 * with its own Retry, exactly as before). Only a failed lengths list errors the card.
 *
 * **The drawer** hosts the existing editors unchanged ({@link StaffPriceEditor},
 * {@link WeeklyPriceEditor}, {@link DatePriceEditor}) and the {@link EffectivePriceView}
 * check, for the length picked at the top. Those editors save one row at a time (each day,
 * date or coach is its own upsert/delete, exactly as before), so this drawer's footer is a
 * single Done rather than a Save; closing it re-reads the card.
 */
import { useCallback, useId, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { formatMoney } from '@/utils/v2Format';
import {
  getDatePrices,
  getWeeklyPrices,
  listVariationStaffPrices,
  listVariations,
  type DatePrice,
  type ServiceVariation,
  type VariationDisplay,
  type VariationStaffPrice,
  type WeeklyPrice,
} from '@/api/services';
import { ScreenError } from '@/components/ScreenError';
import type { IdNameOption } from './AssignmentEditor';
import { StaffPriceEditor } from './StaffPriceEditor';
import { WeeklyPriceEditor } from './WeeklyPriceEditor';
import { DatePriceEditor } from './DatePriceEditor';
import { EffectivePriceView } from './EffectivePriceView';
import { EditDrawer } from './EditDrawer';
import { SummaryCard, SummaryRow } from './SummaryCard';

export interface SpecialPricesCardProps {
  serviceId: string;
  /** The service's coaches (only these may carry a coach price). */
  staffOptions: IdNameOption[];
  variationDisplay?: VariationDisplay | undefined;
  /** Bumped by the Lengths card on any length change, so this card re-reads. */
  lengthsVersion: number;
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface LengthPrices {
  variation: ServiceVariation;
  weekly: WeeklyPrice[];
  dates: DatePrice[];
  staff: VariationStaffPrice[];
  /** One of this length's price reads failed — its prices are unknown, not empty. */
  failed: boolean;
}

/** `_version` only re-keys the read (the Lengths card bumps it). */
async function loadSpecialPrices(serviceId: string, _version: number): Promise<LengthPrices[]> {
  const variations = await listVariations(serviceId);
  return Promise.all(
    variations.map(async (variation) => {
      try {
        const [weekly, dates, staff] = await Promise.all([
          getWeeklyPrices(serviceId, variation.id),
          getDatePrices(serviceId, variation.id),
          listVariationStaffPrices(serviceId, variation.id),
        ]);
        return { variation, weekly, dates, staff, failed: false };
      } catch {
        return { variation, weekly: [], dates: [], staff: [], failed: true };
      }
    }),
  );
}

export function SpecialPricesCard({
  serviceId,
  staffOptions,
  variationDisplay = 'duration',
  lengthsVersion,
}: SpecialPricesCardProps) {
  const showToast = useToast();
  const { currency } = useTenantCurrency();
  const ids = useId();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string>('');
  // Bumped by every editor write so the effective-price check re-resolves (DNKADM-0304 U3).
  const [priceVersion, setPriceVersion] = useState(0);

  const prices = useResource(
    // `lengthsVersion` is a re-read trigger only.
    useCallback(() => loadSpecialPrices(serviceId, lengthsVersion), [serviceId, lengthsVersion]),
    [serviceId, lengthsVersion],
  );

  const rows = useMemo(() => {
    const list = prices.data ?? [];
    return variationDisplay === 'type'
      ? list
      : [...list].sort((a, b) => a.variation.durationMinutes - b.variation.durationMinutes);
  }, [prices.data, variationDisplay]);

  const nameById = useMemo(() => new Map(staffOptions.map((s) => [s.id, s.name || s.id])), [staffOptions]);
  const money = (n: number) => (currency ? formatMoney(n, currency) : n.toFixed(2));

  // A length whose read failed is listed (as unknown), so the card never collapses over it.
  const withPrices = rows.filter(
    (r) => r.failed || r.weekly.length + r.dates.length + r.staff.length > 0,
  );
  const lengths = rows.map((r) => r.variation);
  const current = lengths.find((v) => v.id === picked) ?? lengths[0];

  const empty =
    prices.status === 'ready' && withPrices.length === 0
      ? lengths.length === 0
        ? 'No special prices. Add a length first.'
        : 'No special prices'
      : undefined;

  return (
    <>
      <SummaryCard
        title="Special prices"
        onEdit={() => setOpen(true)}
        editDisabled={prices.status !== 'ready' || lengths.length === 0}
        empty={empty}
      >
        {prices.status === 'loading' && <Skeleton variant="rounded" height={60} />}
        {prices.status === 'error' && (
          <ScreenError error={prices.error} what="the special prices" onRetry={prices.refetch} />
        )}
        {prices.status === 'ready' &&
          withPrices.map((r, i) => {
            const parts: string[] = [];
            for (const w of [...r.weekly].sort((a, b) => a.dayOfWeek - b.dayOfWeek)) {
              parts.push(`${DAY_SHORT[w.dayOfWeek] ?? w.dayOfWeek} ${money(w.price)}`);
            }
            for (const d of [...r.dates].sort((a, b) => a.date.localeCompare(b.date))) {
              parts.push(`${d.date} ${money(d.price)}`);
            }
            for (const s of r.staff) {
              parts.push(`${nameById.get(s.staffId) ?? 'A coach'} ${money(s.price)}`);
            }
            return (
              <SummaryRow
                key={r.variation.id}
                label={r.variation.name || r.variation.id}
                value={r.failed ? 'Couldn’t load its special prices' : parts.join(' · ')}
                last={i === withPrices.length - 1}
              />
            );
          })}
      </SummaryCard>

      <EditDrawer
        open={open}
        title="Special prices"
        doneOnly
        onClose={() => {
          setOpen(false);
          prices.refetch();
        }}
      >
        {current && (
          <Stack spacing={2}>
            {lengths.length > 1 && (
              <FormControl size="small" fullWidth>
                <InputLabel id={`${ids}-length`}>Length</InputLabel>
                <Select
                  labelId={`${ids}-length`}
                  label="Length"
                  value={current.id}
                  onChange={(e) => setPicked(e.target.value)}
                  slotProps={{ input: { 'aria-label': 'Length' } }}
                >
                  {lengths.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name || v.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {lengths.length === 1 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Prices for {current.name || current.id}.
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Each price below saves on its own row.
            </Typography>
            <Box key={current.id}>
              <WeeklyPriceEditor
                serviceId={serviceId}
                variationId={current.id}
                basePrice={current.price}
                currency={currency}
                showToast={showToast}
                onChanged={() => setPriceVersion((v) => v + 1)}
              />
              <Divider sx={{ my: 2 }} />
              <DatePriceEditor
                serviceId={serviceId}
                variationId={current.id}
                basePrice={current.price}
                currency={currency}
                showToast={showToast}
                onChanged={() => setPriceVersion((v) => v + 1)}
              />
              <Divider sx={{ my: 2 }} />
              <StaffPriceEditor
                serviceId={serviceId}
                variationId={current.id}
                staffOptions={staffOptions}
                currency={currency}
                showToast={showToast}
                onChanged={() => setPriceVersion((v) => v + 1)}
              />
              <Divider sx={{ my: 2 }} />
              <EffectivePriceView
                serviceId={serviceId}
                variationId={current.id}
                basePrice={current.price}
                staffOptions={staffOptions}
                currency={currency}
                priceVersion={priceVersion}
              />
            </Box>
          </Stack>
        )}
      </EditDrawer>
    </>
  );
}
